// Medical Portal — Patient Reports page
//
// Data comes from an external Google Cloud Function that returns one row
// per SERVICE. Same patient visit with 4 tests → 4 rows. We group by
// `VisitNo` so the UI shows one card per visit with all the ordered tests
// bundled inside.
//
// "Instant" UX behaviour (as requested):
//   • RTK Query caches each page for an hour — jumping back to a
//     previously viewed page renders instantly from cache.
//   • Classic click-to-paginate: 20 records per page, numbered buttons
//     at the bottom. Clicking a page fetches just that page's 20 rows.
//   • Skeletons show whenever a page is in flight and we have nothing
//     cached yet for it — that includes first load AND subsequent page
//     clicks where the cache is empty.
//   • Clicking a card opens the shared ReportDetailModal (the same one
//     the Overview "Recent reports" rows use) — header + parameter
//     tables + a "Raise Query" CTA that hands the visit off to the
//     RaiseQuery screen via the parent's onNavigate.

import { useEffect, useMemo, useState } from 'react';
import ReportDetailModal from './ReportDetailModal';
import { rowsToCsv, downloadCsv, fetchAllRows } from './exportVisits';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  MP, MONO,
  SectionCard, CardHeader,
  groupByVisit, prettyDate,
} from './shared';
import { useAuth } from '../../../context/AuthContext';
import {
  useGetSalesByCustomerQuery,
  useGetSalesByCustomerAndDoctorQuery,
  useLazyGetSalesByCustomerQuery,
  useLazyGetSalesByCustomerAndDoctorQuery,
} from '../../../store/salesApi';
import { prefetchReportPdfs, prefetchVisit } from '../../../utils/prefetchReports';

// Test options built from what's actually in the data — no stale enum.
const uniqueTests = (visits) => {
  const set = new Set();
  visits.forEach((v) => v.tests.forEach((t) => t.name && set.add(t.name)));
  return Array.from(set).sort();
};


const PatientReports = ({ onNavigate }) => {
  const { user } = useAuth();
  const clientId = user?.client_id;
  // Doctor portal flips this — centre admins see every visit under
  // their client_id, doctors see only their own DoctorID rows.
  const isDoctor = user?.user_type === 'doctor';
  const itdoseDoctorId = user?.doctor?.itdose_doctor_id;

  // Detail modal — same shape Overview uses. `id` is the visit number
  // (ReportDetailModal looks the row up by it in its mock dictionary);
  // `visit` carries the patient + tests so the modal header + the
  // "Raise Query" prefill don't need to re-fetch.
  const [reportModal, setReportModal] = useState({ open: false, id: null, visit: null });
  const closeReportModal = () => setReportModal({ open: false, id: null, visit: null });
  const openReport = (v) => setReportModal({ open: true, id: v.visitNo, visit: v });

  // Classic paginate: one page at a time. Each page arg is cached
  // separately in RTK Query, so jumping back to a viewed page is
  // instant. Resets to 1 when the customerId changes.
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [clientId]);

  // ─── Filters ──────────────────────────────────────────────────
  // All four are applied SERVER-SIDE now, over the customer's whole
  // history, then paginated. They used to be filtered here in the
  // browser over the ~20 rows of the current page, which made the
  // search box look broken: a patient on page 400 of 5,339 simply
  // never appeared, and an empty result meant "not on this page"
  // rather than "not found".
  const [search, setSearch] = useState('');
  const [date, setDate] = useState('');
  const [testFilter, setTestFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Debounced copy of the search box. Without this every keystroke is a
  // request; 400ms is long enough to finish a word, short enough to feel
  // immediate.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change starts a new result set, so page 2 of the old one is
  // meaningless — go back to the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, date, testFilter, statusFilter]);

  // The date picker gives YYYY-MM-DD; the API wants a from/to window, and a
  // single day is just both ends set to it.
  const serverFilters = {
    search:   debouncedSearch,
    test:     testFilter,
    status:   statusFilter,
    fromDate: date || '',
    toDate:   date || '',
  };

  // Both hooks run unconditionally (React hook rule) but only one
  // fires its request — the `skip` flag tells RTK Query to no-op the
  // other. Whichever hook is active drives the visible table.
  const centreQuery = useGetSalesByCustomerQuery(
    { customerId: clientId, page, ...serverFilters },
    { skip: !clientId || isDoctor },
  );
  const doctorQuery = useGetSalesByCustomerAndDoctorQuery(
    { customerId: clientId, doctorId: itdoseDoctorId, page, ...serverFilters },
    { skip: !clientId || !isDoctor || !itdoseDoctorId },
  );
  const { data, isLoading, isFetching } = isDoctor ? doctorQuery : centreQuery;

  const visits = useMemo(() => groupByVisit(data?.data || []), [data]);
  // Built from the API's facets — the distinct tests across the customer's
  // whole history. Deriving them from `visits` meant the dropdown's contents
  // changed every time you paginated, which makes it useless as a filter.
  // Falls back to the page's own tests if an older API build sends no facets.
  const testOptions = useMemo(
    () => data?.facets?.tests || uniqueTests(visits),
    [data, visits],
  );

  // Warm the backend PDF cache for the top visible visit cards so the
  // user's click flow opens in <1s instead of waiting 4-8s on labreportnew.
  useEffect(() => {
    if (visits.length > 0) prefetchReportPdfs(visits);
  }, [visits]);

  const isFiltered = Boolean(debouncedSearch || date || testFilter || statusFilter);

  // ─── Export ───────────────────────────────────────────────────
  // Exports the CURRENT FILTERED SET across every page, not the 20 rows on
  // screen — with 5,000+ pages a one-page export would be pointless. The
  // lazy hooks let us page on demand instead of subscribing.
  const [fetchCentrePage] = useLazyGetSalesByCustomerQuery();
  const [fetchDoctorPage] = useLazyGetSalesByCustomerAndDoctorQuery();
  const [exporting, setExporting] = useState(false);
  const [exportedCount, setExportedCount] = useState(0);
  const [toast, setToast] = useState({ open: false, msg: '', tone: 'info' });

  const onExport = async () => {
    if (exporting || !clientId) return;
    setExporting(true);
    setExportedCount(0);
    try {
      const fetchPage = isDoctor ? fetchDoctorPage : fetchCentrePage;
      const baseArgs = isDoctor
        ? { customerId: clientId, doctorId: itdoseDoctorId, ...serverFilters }
        : { customerId: clientId, ...serverFilters };

      const rows = await fetchAllRows(fetchPage, baseArgs, setExportedCount);
      if (rows.length === 0) {
        setToast?.({ open: true, msg: 'Nothing to export for these filters.', tone: 'info' });
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(
        rowsToCsv(rows),
        `patient-reports-${clientId}-${isFiltered ? 'filtered-' : ''}${stamp}.csv`,
      );
    } catch (e) {
      setToast?.({ open: true, msg: 'Export failed. Please try again.', tone: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // The server already applied every filter, so what came back IS the result
  // set for this page. No second pass here — filtering again locally would
  // silently re-break the thing this change fixed.
  const filtered = visits;

  // Skeletons fire whenever a fetch is in flight and we have nothing
  // cached for the current page — covers both first load and page
  // clicks that miss the cache.
  const showSkeletons = isFetching || isLoading;

  // Pagination meta — drives the numbered buttons at the bottom.
  const meta = data?.meta;
  const totalPages   = meta?.totalPages || 0;
  const totalMatched = meta?.totalMatched || 0;
  const currentPage  = meta?.currentPage || page;

  const today = new Date();
  const todayLine = today
    .toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
    .toUpperCase();

  return (
    <Box>
      {/* ── Filter bar — AIMS `.filters`: the controls sit on their own
             sheet of paper rather than loose on the canvas, so the register
             below reads as the next thing down rather than as more of the
             same. ── */}
      <Box
        sx={{
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: MP.radius,
          boxShadow: MP.shadow,
          p: '13px',
          mb: 1.75,
        }}
      >
      <Grid container spacing={1.25}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth size="small"
            placeholder="Search patient name, UHID, visit no, or test…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={filterInputSx}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <TextField
            type="date" fullWidth size="small"
            label="Bill date"
            InputLabelProps={{ shrink: true }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            sx={filterInputSx}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            select fullWidth size="small"
            label="Test"
            InputLabelProps={{ shrink: true }}
            value={testFilter}
            onChange={(e) => setTestFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={filterInputSx}
          >
            <MenuItem value="">All Tests</MenuItem>
            {testOptions.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            select fullWidth size="small"
            label="Status"
            InputLabelProps={{ shrink: true }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={filterInputSx}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="DeActive">DeActive</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      </Box>

      {/* ── Register ── the title, the count and the export action all live
             on the card's own title bar, AIMS-style, instead of floating
             above it. The export button is painted light-on-dark: anything
             in `right` renders ON that bar. */}
      <SectionCard>
      <CardHeader
        title={`Patient Reports · ${todayLine}`}
        subtitle={`${filtered.length} ${filtered.length === 1 ? 'visit' : 'visits'} shown${
          totalPages
            ? ` · page ${currentPage} of ${totalPages} · ${totalMatched} ${
              isFiltered ? 'matching' : 'total'} services`
            : ''}`}
        right={(
        <Button
          size="small"
          variant="outlined"
          onClick={onExport}
          disabled={exporting || totalMatched === 0}
          sx={{
            borderRadius: MP.radius,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 12.5,
            // Solid white, not a translucent wash: at 92% the dark title
            // bar showed through and the button read as greyed-out even
            // when it was live.
            bgcolor: MP.white,
            borderColor: MP.white,
            color: MP.dark,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: MP.white, borderColor: MP.white, color: MP.teal },
            // Disabled still has to look like a button, just an inert one.
            // Measured over the title bar these flatten to #BCCFCE on
            // #4C565A - 4.65:1, so "nothing to export" stays readable
            // while reading as clearly off.
            '&.Mui-disabled': {
              bgcolor: 'rgba(255,255,255,.70)',
              borderColor: 'rgba(255,255,255,.70)',
              color: 'rgba(16,21,28,.65)',
            },
          }}
        >
          {exporting
            // A 100k-row export is several round-trips; show it moving so it
            // doesn't look frozen.
            ? `Exporting… ${exportedCount.toLocaleString()}`
            // Say WHAT will be exported — with filters on, "All" alone is a lie.
            : `⬇ Export ${isFiltered ? 'filtered' : 'all'}${
                totalMatched ? ` (${totalMatched.toLocaleString()})` : ''}`}
        </Button>
        )}
      />

      {/* ── Register ── the same table the dashboard's Recent
             Requisitions uses. A grid of cards wastes most of its width on
             whitespace and lets nothing line up: with one visit per card
             you cannot run your eye down a column of dates or amounts,
             which is the whole job of this screen. */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: 13,
            minWidth: 820,
            '& td': {
              textAlign: 'left',
              padding: '10px 12px',
              borderBottom: `1px solid ${MP.borderSoft}`,
              verticalAlign: 'middle',
            },
            // Outer gutters. Without these the first and last columns sit
            // hard against the card's edge — the visit number and the
            // amount looked stuck to the frame rather than set inside it.
            // 20px matches the card's own body padding.
            '& td:first-of-type, & th:first-of-type': { paddingLeft: '20px' },
            '& td:last-of-type, & th:last-of-type': { paddingRight: '20px' },
            '& th': {
              textAlign: 'left',
              padding: '11px 12px 9px',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: MP.white,
              boxShadow: `inset 0 -1px 0 ${MP.lnBot}`,
              fontFamily: MONO,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '.02em',
              color: MP.dark,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            },
            '& tbody tr:hover': { bgcolor: MP.rowHover },
          }}
        >
          <thead>
            <tr>
              {/* The right-hand trio is sized in PERCENTAGES, not pixels.
                  With fixed widths every spare pixel on a wide screen went
                  to Patient · Tests, which opened a dead gap between the
                  test chips and Age / Sex. Percentages let the three grow
                  with the table so the slack is shared instead of pooling
                  in one column. */}
              <th style={{ width: 172 }}>Visit · UHID</th>
              <th>Patient · Tests</th>
              <th style={{ width: '11%', minWidth: 96 }}>Age / Sex</th>
              <th style={{ width: '13%', minWidth: 118 }}>Bill date</th>
              <th style={{ width: '13%', minWidth: 112, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {showSkeletons && Array.from({ length: 10 }).map((_, i) => (
              <ReportRowSkeleton key={`skeleton-${i}`} />
            ))}

            {!showSkeletons && filtered.map((v) => (
              <ReportRow key={`${v.visitNo}-${v.billNo}`} visit={v} onClick={() => openReport(v)} />
            ))}

            {!showSkeletons && filtered.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 36, color: MP.textMuted }}>
                  {visits.length === 0
                    ? (clientId
                      ? 'No patient reports found for your client id.'
                      : 'Your account is missing a Client ID — contact admin to link one.')
                    : 'No reports match the selected filters.'}
                </td>
              </tr>
            )}
          </tbody>
        </Box>
      </Box>

      {/* Compact pagination — shows a sliding window of 3 page
          numbers centered on the current page, with Prev/Next arrows.
          When you're on page 3 of 6 the window renders `2 [3] 4`, so
          the "next" page is always visible without cluttering the
          row with every number. Cache still lives in RTK Query, so
          revisiting a page is instant. */}
      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3, mb: 1 }}>
          <CompactPagination
            count={totalPages}
            page={currentPage}
            disabled={isFetching}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </Stack>
      )}
      </SectionCard>

      {/* Card-click report modal — same component the Overview uses.
          The "Raise Query" button inside the modal hops to the
          RaiseQuery screen via the parent dashboard's `go()` handler
          (forwarded here as `onNavigate`), pre-filling the visit so
          the agent doesn't have to re-pick the patient. */}
      <ReportDetailModal
        open={reportModal.open}
        onClose={closeReportModal}
        reportId={reportModal.id}
        visit={reportModal.visit}
        onRaiseQuery={(visit) => {
          closeReportModal();
          onNavigate?.('new-complaint', { visit });
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.tone}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

// ─── Compact pagination ───────────────────────────────────────────
// Sliding window of N page buttons (default 3) centered on the
// current page, clamped at the edges. Prev/Next arrows flank the
// numbers. Example with count=6 and a 3-wide window:
//   page 1:  ‹ [1] 2 3 ›
//   page 2:  ‹ 1 [2] 3 ›
//   page 3:  ‹ 2 [3] 4 ›     ← page 4 "unlocks" when you reach 3
//   page 4:  ‹ 3 [4] 5 ›
//   page 5:  ‹ 4 [5] 6 ›
//   page 6:  ‹ 4 5 [6] ›
const PAGE_WINDOW = 3;

const CompactPagination = ({ count, page, onChange, disabled }) => {
  if (count <= 1) return null;

  // Build the sliding window. Start by putting `page` in the middle,
  // then push the window inward if it would fall off either edge.
  let start = Math.max(1, page - Math.floor(PAGE_WINDOW / 2));
  let end = Math.min(count, start + PAGE_WINDOW - 1);
  start = Math.max(1, end - PAGE_WINDOW + 1);

  const pages = [];
  for (let i = start; i <= end; i += 1) pages.push(i);

  const atFirst = page <= 1;
  const atLast  = page >= count;

  const arrowSx = {
    width: 36, height: 36, borderRadius: '6px',
    boxShadow: MP.shadow,
    border: `1px solid ${MP.border}`,
    bgcolor: MP.white, color: MP.text,
    '&:hover': { borderColor: MP.teal, bgcolor: MP.tealLight, color: MP.tealDark },
    '&.Mui-disabled': { bgcolor: MP.bg, color: MP.border, borderColor: MP.border },
  };

  const pageBtnSx = (active) => ({
    minWidth: 36,
    height: 36,
    p: 0,
    borderRadius: '6px',
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 700,
    fontSize: 13,
    border: `1px solid ${active ? MP.teal : MP.border}`,
    bgcolor: active ? MP.teal : MP.white,
    color: active ? '#fff' : MP.text,
    boxShadow: active ? '0 2px 8px rgba(16,21,28,.10)' : 'none',
    transition: 'all .15s',
    '&:hover': {
      bgcolor: active ? MP.tealDark : MP.tealLight,
      borderColor: MP.teal,
      color: active ? '#fff' : MP.tealDark,
    },
    '&.Mui-disabled': { opacity: 0.6 },
  });

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <IconButton
        size="small"
        disabled={disabled || atFirst}
        onClick={() => onChange(page - 1)}
        aria-label="Previous page"
        sx={arrowSx}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      {pages.map((p) => (
        <Button
          key={p}
          disableRipple
          disabled={disabled}
          onClick={() => onChange(p)}
          sx={pageBtnSx(p === page)}
        >
          {p}
        </Button>
      ))}

      <IconButton
        size="small"
        disabled={disabled || atLast}
        onClick={() => onChange(page + 1)}
        aria-label="Next page"
        sx={arrowSx}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

// ─── Card ──────────────────────────────────────────────────────────
// How many test names to spell out before collapsing the rest into a
// count. Three, not one: a single name plus "+6 more" tells you almost
// nothing about what the visit was for, and the column has the room.
const TESTS_SHOWN = 3;

const ReportRow = ({ visit, onClick }) => {
  const names = visit.tests.map((t) => t.name).filter(Boolean);
  const shown = names.slice(0, TESTS_SHOWN);
  const extra = Math.max(0, names.length - shown.length);
  return (
    <Box
      component="tr"
      onClick={onClick}
      // Hover / touch starts the PDF warm in the background. By the
      // time the user opens the modal and clicks Download, the
      // backend cache is hot.
      onMouseEnter={() => prefetchVisit(visit?.visitNo)}
      onTouchStart={() => prefetchVisit(visit?.visitNo)}
      sx={{ cursor: 'pointer' }}
    >
      <td>
        <Box sx={{ fontFamily: MONO, fontSize: 11, color: MP.text, fontWeight: 700 }}>
          {visit.visitNo || '—'}
        </Box>
        <Box sx={{ fontFamily: MONO, fontSize: 10.5, color: MP.teal, fontWeight: 700, mt: '2px' }}>
          {visit.uhid || '—'}
        </Box>
      </td>
      <td>
        <Box sx={{ fontWeight: 600, color: MP.text }}>{visit.patient || 'Unknown'}</Box>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: '4px' }}>
          {shown.map((t) => (
            <Box
              key={t}
              sx={{
                bgcolor: MP.tealLight, color: MP.teal,
                fontSize: 11, lineHeight: 1.6,
                px: '8px', py: '2px', borderRadius: '20px',
                maxWidth: 210, overflow: 'hidden',
                whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              }}
            >
              {t}
            </Box>
          ))}
          {extra > 0 && (
            <Box
              sx={{
                bgcolor: MP.greyLight, color: MP.grey,
                fontSize: 11, lineHeight: 1.6,
                px: '8px', py: '2px', borderRadius: '20px',
              }}
            >
              +{extra} more
            </Box>
          )}
          {names.length === 0 && (
            <Box sx={{ fontSize: 11.5, color: MP.ink3 }}>No tests listed</Box>
          )}
        </Stack>
      </td>
      <td>
        <Box sx={{ fontFamily: MONO, fontSize: 11.5, color: MP.textMuted, whiteSpace: 'nowrap' }}>
          {visit.age || '—'}{visit.gender ? ` / ${visit.gender[0]}` : ''}
        </Box>
      </td>
      <td>
        <Box sx={{ fontFamily: MONO, fontSize: 11.5, color: MP.textMuted, whiteSpace: 'nowrap' }}>
          {prettyDate(visit.billDate)}
        </Box>
      </td>
      <td style={{ textAlign: 'right' }}>
        <Box sx={{
          fontFamily: MONO, fontSize: 13, fontWeight: 700,
          color: MP.dark, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        }}>
          ₹{(visit.totalAmount || 0).toLocaleString('en-IN')}
        </Box>
      </td>
    </Box>
  );
};

// NOTE: no Status column, deliberately. The feed's `Status` is inverted at
// source — IT Dose confirmed a LIVE test arrives as "DeActive" — so it read
// "DeActive" on virtually every visit. `ReportReleased` would give real
// readiness but is not in this response yet.

// ─── Skeleton row, shaped like the row it stands in for ───────────
const ReportRowSkeleton = () => (
  <Box component="tr">
    <td><Skeleton variant="text" width={96} height={13} /><Skeleton variant="text" width={72} height={12} /></td>
    <td>
      <Skeleton variant="text" width="42%" height={16} />
      <Stack direction="row" spacing={0.5} sx={{ mt: '4px' }}>
        <Skeleton variant="rounded" width={78} height={18} sx={{ borderRadius: '20px' }} />
        <Skeleton variant="rounded" width={62} height={18} sx={{ borderRadius: '20px' }} />
        <Skeleton variant="rounded" width={88} height={18} sx={{ borderRadius: '20px' }} />
      </Stack>
    </td>
    <td><Skeleton variant="text" width={58} height={13} /></td>
    <td><Skeleton variant="text" width={86} height={13} /></td>
    <td style={{ textAlign: 'right' }}><Skeleton variant="text" width={64} height={13} sx={{ ml: 'auto' }} /></td>
  </Box>
);

// Filter-bar inputs. Lost when the card grid was replaced — it sat between
// the card and the skeleton — so it is restored here, with the AIMS
// hairline rule and focus ring the rest of the portal uses.
const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: MP.white,
    borderRadius: MP.radius,
    fontSize: 13,
    height: 40,
    '& fieldset': { borderColor: MP.border, borderWidth: 1 },
    '&:hover fieldset': { borderColor: MP.ink3 },
    '&.Mui-focused fieldset': { borderColor: MP.teal, borderWidth: 1 },
    '&.Mui-focused': { boxShadow: MP.focus },
  },
  '& .MuiInputLabel-root': { fontSize: 12 },
};

export default PatientReports;
